import React, {
  FC,
  ReactElement,
  useEffect,
  useState,
  useRef,
  MouseEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ROOT, LIBRARY, LOGIN, ACCOUNT } from "routes";
import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import HomeIcon from "@mui/icons-material/Home";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LoginIcon from "@mui/icons-material/Login";
// import SettingsIcon from '@mui/icons-material/Settings';
// Removed nested collapse import
// import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
// Removed unused Switch import
import Divider from "@mui/material/Divider";
// Removed nested ListSubheader import
import { writeLoginRelease } from "api";
import { useActor } from "contexts/ActorContext";
// Hooks for data fetching and debounce
import { useTitleSuggestQuery } from "hooks/apiHooks";
import useDebouncedValue from "../hooks/useDebouncedValue";
import useAuthRedirect from "../hooks/useAuthRedirect";
import { usePlayback } from "contexts/WatchContext";
import Badge from "@mui/material/Badge";

/**
 * Represents a title suggestion result from the API.
 */
export interface TitleSuggestion {
  trackTitle: string;
  actorTitle: string;
  musicVideo: string;
}

interface TitleToolbarProps {
  onInputSelected(titleId: string): void;
  /** Open the queue drawer */
  onOpenDrawer(): void;
}

/**
 * The search autocomplete bar for titles.
 */
const TitleToolbar: FC<TitleToolbarProps> = ({
  onInputSelected,
  onOpenDrawer,
}): ReactElement => {
  const theme = useTheme();
  // Actor (user) metadata from context
  const { actor, setActor } = useActor();
  // Profile menu state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openProfileMenu = Boolean(anchorEl);

  const handleProfileOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleProfileClose = () => {
    setAnchorEl(null);
  };
  const handleSignOut = async () => {
    handleProfileClose();
    try {
      await writeLoginRelease();
    } catch (e) {
      console.error("Sign out failed", e);
    }
    // Clear actor and reload page so public access or login redirect applies as needed
    setActor(undefined);
    window.location.reload();
  };
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation([
    "titleWelcome",
    "titleLibrary",
    "titleToolbar",
    "actorAccount",
  ]);
  const location = useLocation();
  const navigate = useNavigate();
  // Active page detection for highlights
  const pathname = location.pathname;
  const isHome = pathname === ROOT;
  const isLibrary = pathname === LIBRARY;

  const [titleInput, setTitleInput] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<
    string | TitleSuggestion | null
  >(null);
  // Debounce the input before firing queries
  const debouncedInput = useDebouncedValue(titleInput, 200);
  // React Query hook for title suggestions
  const {
    data: suggestData,
    isLoading: loading,
    error,
  } = useTitleSuggestQuery(debouncedInput);
  // Redirect to login on auth error
  useAuthRedirect(error);
  const hasInput = debouncedInput.trim().length > 0;
  const videoBlock = hasInput
    ? (suggestData?.videoMetadata?.videoMetadata ?? [])
    : [];
  const showLoading = loading && hasInput;
  useEffect(() => {
    setTitleInput("");
    setSelectedOption(null);
  }, [location.pathname]);
  // Determine active nav path for highlighting

  const onInputChange = (_: any, value: string): void => {
    setTitleInput(value);
    setSelectedOption(null);
  };

  /**
   * Handles selection or enter of an option.
   */
  const onTitleChange = (
    _: React.SyntheticEvent,
    value: string | TitleSuggestion | null,
  ): void => {
    setSelectedOption(value);
    if (value && typeof value !== "string") {
      onInputSelected(value.musicVideo);
    }
  };

  /**
   * Label extractor: choose track-actor or actor-track based on query start match.
   */
  const getOptionLabel = (option: string | TitleSuggestion): string => {
    if (typeof option === "string") {
      return option;
    }
    const input = titleInput.trim().toLowerCase();
    const trackActor = `${option.trackTitle} ${option.actorTitle}`;
    const actorTrack = `${option.actorTitle} ${option.trackTitle}`;
    const taLower = trackActor.toLowerCase();
    const atLower = actorTrack.toLowerCase();
    if (input && taLower.startsWith(input)) {
      return trackActor;
    }
    if (input && atLower.startsWith(input)) {
      return actorTrack;
    }
    return trackActor;
  };

  const { facadeQueue, drawerOpen } = usePlayback();
  // Badge for queue length
  const badgeCount = facadeQueue.length;
  // Cycle through fixed badge colors when the first queue item changes
  const badgeColors = [theme.palette.secondary.main, theme.palette.info.main];
  const [badgeColorIdx, setBadgeColorIdx] = useState(0);
  const prevFirstKey = useRef<string | undefined>(undefined);
  const firstKey = facadeQueue[0]?.musicVideo;
  useEffect(() => {
    if (firstKey && firstKey !== prevFirstKey.current) {
      setBadgeColorIdx((idx) => (idx + 1) % badgeColors.length);
    }
    prevFirstKey.current = firstKey;
  }, [firstKey]);
  const badgeColor = badgeColors[badgeColorIdx];
  // same drawer widths as Layout: mini=72px, full=256px
  const fullDrawerWidth = theme.spacing(32);
  // Fixed AppBar: shifts right when drawer opens, stays above drawer
  return (
    <AppBar
      position="fixed"
      elevation={4}
      sx={{
        background: "linear-gradient(to right, #ef233c, #1e40af)",
        color: "#FFFFFF",
        zIndex: theme.zIndex.drawer + 1,
        left: isDesktop && drawerOpen ? fullDrawerWidth : 0,
        width:
          isDesktop && drawerOpen ? `calc(100% - ${fullDrawerWidth})` : "100%",
        transition: theme.transitions.create(["left", "width"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      {/* Top row: drawer, logo, centered search, user icon */}
      <Toolbar
        sx={{
          px: { xs: 1, md: 4 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, md: 4 },
        }}
      >
        {/* Drawer toggle: hamburger on desktop, TITLE logo on mobile */}
        {isDesktop ? (
          <>
            {/* Drawer toggle with queue count badge when closed */}
            {facadeQueue.length > 0 && !drawerOpen ? (
              <Badge
                badgeContent={badgeCount}
                max={100}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  "& .MuiBadge-badge": {
                    bgcolor: badgeColor,
                    color: theme.palette.common.white,
                  },
                }}
              >
                <IconButton
                  edge="start"
                  size="large"
                  onClick={onOpenDrawer}
                  sx={{
                    color: "#FFFFFF",
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <MenuIcon fontSize="medium" />
                </IconButton>
              </Badge>
            ) : (
              <IconButton
                edge="start"
                size="large"
                onClick={onOpenDrawer}
                disabled={facadeQueue.length === 0 || drawerOpen}
                sx={{
                  color: "#FFFFFF",
                  backgroundColor: "transparent",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
              >
                <MenuIcon fontSize="medium" />
              </IconButton>
            )}
            {/* Brand identity: click logo navigates to Welcome */}
            <Box
              sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={() => navigate(ROOT)}
              aria-label={t("welcome")}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "#FFFFFF",
                }}
              >
                {t("title")}
              </Box>
            </Box>
          </>
        ) : /* Mobile drawer toggle with badge */
        facadeQueue.length > 0 && !drawerOpen ? (
          <Badge
            badgeContent={badgeCount}
            max={100}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiBadge-badge": {
                bgcolor: badgeColor,
                color: theme.palette.common.white,
              },
            }}
          >
            <IconButton
              size="large"
              onClick={onOpenDrawer}
              aria-label={t("queue")}
              sx={{
                ml: 0,
                color: "#FFFFFF",
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              <MenuIcon fontSize="medium" />
            </IconButton>
          </Badge>
        ) : (
          <IconButton
            size="large"
            onClick={onOpenDrawer}
            disabled={facadeQueue.length === 0 || drawerOpen}
            aria-label={t("queue")}
            sx={{
              ml: 0,
              color: "#FFFFFF",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <MenuIcon fontSize="medium" />
          </IconButton>
        )}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Autocomplete
            disablePortal
            fullWidth
            slotProps={{
              popper: {
                // Force popper width to match input with a minimum, avoid overflow
                modifiers: [
                  {
                    name: "widthMatch",
                    enabled: true,
                    phase: "beforeWrite",
                    requires: ["computeStyles"],
                    fn: ({ state }) => {
                      const refW = state.rects.reference.width;
                      const minW = parseFloat(theme.spacing(30) as string);
                      const widthVal = Math.max(refW, minW);
                      state.styles.popper.width = `${widthVal}px`;
                    },
                  },
                ],
                sx: {
                  zIndex: theme.zIndex.modal,
                  maxWidth: "100vw",
                  overflowX: "hidden",
                },
              },
              paper: {
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "background.paper",
                },
              },
              listbox: {
                sx: {
                  p: 0,
                  maxHeight: "none",
                  "& li": {
                    py: 1,
                    px: 2,
                    whiteSpace: "nowrap",
                  },
                },
              },
            }}
            loading={showLoading}
            // Hide clear button when input is empty
            disableClearable={!titleInput.trim()}
            loadingText={
              showLoading ? (
                <Box sx={{ p: 1, textAlign: "center" }}>
                  <CircularProgress size={20} />
                </Box>
              ) : undefined
            }
            inputValue={titleInput}
            value={selectedOption}
            freeSolo
            options={videoBlock}
            getOptionLabel={getOptionLabel}
            onChange={onTitleChange}
            onInputChange={onInputChange}
            filterOptions={(x) => x}
            sx={{
              width: "100%",
              maxWidth: { md: 500, lg: 600 },
              "& .MuiOutlinedInput-root": {
                bgcolor: "common.white",
                borderRadius: 24,
              },
              "& .MuiAutocomplete-paper": {
                mt: 1,
                boxShadow: 2,
                borderRadius: 24,
                bgcolor: "background.paper",
                maxHeight: "none",
              },
            }}
            renderInput={(params) => {
              // Render search input; intercept Enter to select top suggestion if none chosen
              return (
                <TextField
                  {...params}
                  placeholder={t("musicSearch")}
                  variant="outlined"
                  size="small"
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      selectedOption == null &&
                      videoBlock.length > 0
                    ) {
                      onInputSelected(videoBlock[0].musicVideo);
                      event.preventDefault();
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: { fontWeight: 400, borderRadius: 24, pl: 1, pr: 1 },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { borderRadius: 24 },
                  }}
                />
              );
            }}
            renderOption={(props, option) => {
              const label = getOptionLabel(option);
              const input = titleInput.trim();
              let content: React.ReactNode = label;
              if (typeof option !== "string" && input) {
                const idx = label.toLowerCase().indexOf(input.toLowerCase());
                if (idx >= 0) {
                  const before = label.slice(0, idx);
                  const matchText = label.slice(idx, idx + input.length);
                  const after = label.slice(idx + input.length);
                  content = (
                    <>
                      {before}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {matchText}
                      </Box>
                      {after}
                    </>
                  );
                }
              }
              return (
                <Box component="li" {...props}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SearchIcon color="inherit" />
                    <Typography noWrap sx={{ fontWeight: 400 }}>
                      {content}
                    </Typography>
                  </Stack>
                </Box>
              );
            }}
          />
        </Box>
        {/* Navigation icon buttons for desktop */}
        {isDesktop && (
          <>
            <IconButton
              size="large"
              onClick={() => navigate(ROOT)}
              sx={{
                color: isHome ? theme.palette.warning.main : "#FFFFFF",
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
              aria-label={t("welcome")}
            >
              <HomeIcon fontSize="medium" />
            </IconButton>
            {actor?.actorEmail && (
              <IconButton
                size="large"
                onClick={() => navigate(LIBRARY)}
                sx={{
                  color: isLibrary ? theme.palette.warning.main : "#FFFFFF",
                  backgroundColor: "transparent",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
                aria-label={t("library")}
              >
                <FavoriteIcon fontSize="medium" />
              </IconButton>
            )}
          </>
        )}
        {/* Profile menu */}
        <Menu
          anchorEl={anchorEl}
          open={openProfileMenu}
          onClose={handleProfileClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {/* User email */}
          <MenuItem disabled sx={{ color: "text.primary", fontWeight: "bold" }}>
            {actor?.actorEmail}
          </MenuItem>
          <Divider />
          {/* Account header */}
          <MenuItem
            disableRipple
            sx={{
              color: "text.primary",
              fontWeight: "bold",
              fontSize: "1rem",
              pointerEvents: "none",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            {t("account")}
          </MenuItem>
          {/* Profile information */}
          <MenuItem
            onClick={() => {
              navigate(ACCOUNT, { state: { scrollTo: "profileInformation" } });
              handleProfileClose();
            }}
            sx={{ color: "text.primary", px: 2 }}
          >
            {t("profileInformation")}
          </MenuItem>
          {/* Account preferences */}
          <MenuItem
            onClick={() => {
              navigate(ACCOUNT, { state: { scrollTo: "accountPreferences" } });
              handleProfileClose();
            }}
            sx={{ color: "text.primary", px: 2 }}
          >
            {t("accountPreferences")}
          </MenuItem>
          {/* Data privacy */}
          <MenuItem
            onClick={() => {
              navigate(ACCOUNT, { state: { scrollTo: "dataPrivacy" } });
              handleProfileClose();
            }}
            sx={{ color: "text.primary", px: 2 }}
          >
            {t("dataPrivacy")}
          </MenuItem>
          {/* Get a copy of your data */}
          <MenuItem
            onClick={() => {
              navigate(ACCOUNT, { state: { scrollTo: "getCopyOfYourData" } });
              handleProfileClose();
            }}
            sx={{ color: "text.primary", px: 2 }}
          >
            {t("getCopyOfYourData")}
          </MenuItem>
          {/* Personalization option */}
          <MenuItem
            onClick={() => {
              navigate(ACCOUNT, { state: { scrollTo: "personalization" } });
              handleProfileClose();
            }}
            sx={{ color: "text.primary", px: 2 }}
          >
            {t("personalization")}
          </MenuItem>
          {/* Account management */}
          <MenuItem
            onClick={() => {
              navigate(ACCOUNT, { state: { scrollTo: "accountManagement" } });
              handleProfileClose();
            }}
            sx={{ color: "text.primary", px: 2 }}
          >
            {t("accountManagement")}
          </MenuItem>
          <Divider />
          {/* Sign out */}
          <MenuItem onClick={handleSignOut} sx={{ color: "text.primary" }}>
            {t("signOut")}
          </MenuItem>
        </Menu>
        {/* User profile: avatar if signed in, Sign-In text on desktop, icon on mobile for unauthenticated */}
        {actor && actor.actorEmail ? (
          <IconButton
            size="large"
            onClick={handleProfileOpen}
            aria-label={t("profile")}
            sx={{
              color: "#FFFFFF",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              mr: { xs: 0 },
            }}
          >
            <AccountCircleIcon fontSize="medium" />
          </IconButton>
        ) : isDesktop ? (
          <Button
            size="medium"
            onClick={() => navigate(LOGIN)}
            sx={{
              bgcolor: "grey.100",
              color: "secondary.dark",
              fontWeight: "bold",
              borderRadius: "9999px",
              textTransform: "none",
              px: 2,
              "&:hover": { bgcolor: "grey.200" },
              mx: { xs: 0, md: 2 },
            }}
          >
            {t("signIn")}
          </Button>
        ) : (
          <IconButton
            size="large"
            onClick={() => navigate(LOGIN)}
            aria-label={t("signIn")}
            sx={{
              color: "#FFFFFF",
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              mr: { xs: 0 },
            }}
          >
            <LoginIcon fontSize="medium" />
          </IconButton>
        )}
      </Toolbar>
      {/* Navigation tabs removed for desktop; using icon buttons in top row instead */}
    </AppBar>
  );
};

// We've moved fetching into useTitleSuggestQuery via React Query.

export default TitleToolbar;
